import { fmtClassName } from '../../utils/pdf/format';
import type { Lesson, ProblemFix, TimetableGrid } from '../../services/timetableService';

/**
 * Reading a school day on the client. Mirrors backend services/timetable/grid.ts
 * (the server re-checks every edit; this only drives what the board shows).
 */

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Week order for pickers: Monday first. */
export const WEEK = [1, 2, 3, 4, 5, 6, 0];

export const periodCount = (grid: TimetableGrid) => grid.rows.filter((r) => r.kind === 'period').length;

export const periodsOn = (grid: TimetableGrid, day: number) => {
  const all = periodCount(grid);
  const short = grid.shortDays.find((s) => s.day === day);
  return short ? Math.min(short.periods, all) : all;
};

export const breaksAfter = (grid: TimetableGrid, day: number) => {
  const last = periodsOn(grid, day);
  const after: number[] = [];
  let seen = 0;
  for (const row of grid.rows) {
    if (row.kind === 'period') seen += 1;
    else if (seen > 0 && seen < last) after.push(seen);
  }
  return after;
};

export const fitsGrid = (grid: TimetableGrid, day: number, period: number, span: number) =>
  grid.days.includes(day)
  && period >= 1
  && period + span - 1 <= periodsOn(grid, day)
  && (span === 1 || !breaksAfter(grid, day).includes(period));

export const weeklyPeriods = (grid: TimetableGrid) => grid.days.reduce((n, d) => n + periodsOn(grid, d), 0);

/** Days in week order (Monday first). */
export const orderedDays = (grid: TimetableGrid) => WEEK.filter((d) => grid.days.includes(d));

/** The grid's columns in order: periods, with a break column where the day has one. */
export type GridColumn = { kind: 'period'; period: number; start: string; end: string; label?: string } | { kind: 'break'; label: string; afterPeriod: number };

export const gridColumns = (grid: TimetableGrid): GridColumn[] => {
  const cols: GridColumn[] = [];
  const total = periodCount(grid);
  let seen = 0;
  for (const row of grid.rows) {
    if (row.kind === 'period') cols.push({ kind: 'period', period: ++seen, start: row.start, end: row.end, label: row.label });
    else if (seen > 0 && seen < total) cols.push({ kind: 'break', label: row.label || 'Break', afterPeriod: seen });
  }
  return cols;
};

export const sectionLabel = (l: Pick<Lesson, 'className' | 'sectionName'>) =>
  `${fmtClassName(l.className)}${l.sectionName ? ` ${l.sectionName}` : ''}`;

export const ROOM_TYPE_LABELS: Record<string, string> = {
  classroom: 'Classroom',
  lab: 'Science lab',
  computer_lab: 'Computer lab',
  auditorium: 'Auditorium',
  library: 'Library',
  sports: 'Sports ground',
  other: 'Other',
};

/** Room types a lesson can ask for (anything but an ordinary classroom). */
export const SPECIAL_ROOM_TYPES = Object.keys(ROOM_TYPE_LABELS).filter((t) => t !== 'classroom');

/**
 * A steady colour per subject, so Maths looks the same on every class's grid.
 * Hues are spaced around the wheel; saturation and lightness are tuned per
 * theme mode by the card that uses them.
 */
export const subjectHue = (subjectId: string) => {
  let h = 0;
  for (let i = 0; i < subjectId.length; i += 1) h = (h * 31 + subjectId.charCodeAt(i)) >>> 0;
  return (h * 137.508) % 360;
};

/** Where each kind of problem is fixed. */
export const fixLink = (fix: ProblemFix, sessionId: string): { to: string; label: string } => {
  switch (fix) {
    case 'school_day': return { to: '/app/timetable/setup?tab=day', label: 'School day' };
    case 'lessons': return { to: '/app/timetable/setup?tab=lessons', label: 'Lessons' };
    case 'availability': return { to: '/app/timetable/setup?tab=availability', label: 'Availability' };
    case 'rooms': return { to: '/app/timetable/setup?tab=rooms', label: 'Rooms' };
    case 'curriculum': return { to: `/app/settings/sessions/${sessionId}?tab=curriculum`, label: 'Curriculum' };
    case 'teaching_loads': return { to: `/app/settings/sessions/${sessionId}?tab=teaching`, label: 'Teaching Loads' };
    case 'board': return { to: '/app/timetable/board?view=draft', label: 'Draft' };
  }
};

/** "08:40" → 520 minutes after midnight. */
export const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

/** 520 → "08:40" (wraps past midnight). */
export const fromMinutes = (m: number) => {
  const v = ((Math.round(m) % 1440) + 1440) % 1440;
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};
